import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, ChevronDown, Search } from 'lucide-react';
import {
  createNotification,
  updateNotification,
  Notification,
  NOTIFICATION_TYPES,
} from '../../../services/notifications/notificationsService';
import { TeamMember } from '../../../services/teamMembers/teamMembersService';
import { cn } from '../../../lib/utils';
import { secondaryText, smallTextSize, semiBoldText, primaryText, borderPrimary } from '../../../styling/stylingHelper';
import { useAuth } from '../../../services/authentication/authContext';

// Notification type display info
const NOTIFICATION_TYPE_INFO: Record<string, { label: string; color: string; bgColor: string }> = {
  task_reminder: { label: 'notifications.types.task_reminder', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  change_alert: { label: 'notifications.types.change_alert', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  announcement: { label: 'notifications.types.announcement', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  leadership: { label: 'notifications.types.leadership', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/30' },
};

interface SaveNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  restaurantId: string;
  notification?: Notification;
  onSave: () => Promise<void>;
  teamMembers: TeamMember[];
}

export default function SaveNotificationModal({
  isOpen,
  onClose,
  mode,
  restaurantId,
  notification,
  onSave,
  teamMembers,
}: SaveNotificationModalProps) {
  const { t } = useTranslation();
  const userName = useAuth().backendUser?.name;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notificationType, setNotificationType] = useState<string>('announcement');
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [dueAt, setDueAt] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && notification) {
        setTitle(notification.title);
        setDescription(notification.description);
        setNotificationType(notification.notificationType);
        setAssignedTo(notification.assignedTo || []);
        setDueAt(notification.dueAt ? notification.dueAt.toISOString().slice(0, 16) : '');
      } else {
        // Create mode - start with empty title (will be prefilled if user selects leadership type)
        setTitle('');
        setDescription('');
        setNotificationType('announcement');
        setAssignedTo([]);
        setDueAt('');
      }
      setError(null);
      setSaving(false);
    }
  }, [isOpen, mode, notification]);

  // When type changes to leadership, pre-fill title with user name; clear when switching away
  // Also clear dueAt when switching away from task_reminder
  useEffect(() => {
    if (mode === 'create') {
      if (notificationType === 'leadership' && userName) {
        setTitle(userName);
      } else if (notificationType !== 'leadership') {
        // Clear title only if it matches the user's name (auto-filled)
        setTitle((currentTitle) =>
          currentTitle === userName ? '' : currentTitle
        );
      }
    }
    // Clear dueAt when switching to a type that doesn't support it
    if (notificationType !== 'task_reminder') {
      setDueAt('');
    }
    // The access poll replaces backendUser every 10s; key on the name so typed input survives.
  }, [notificationType, mode, userName]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
        setUserSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter team members by search query
  const filteredTeamMembers = teamMembers.filter((member) => {
    const displayName = member.name || member.email?.split('@')[0] || '';
    const email = member.email || '';
    const query = userSearchQuery.toLowerCase();
    return displayName.toLowerCase().includes(query) || email.toLowerCase().includes(query);
  });

  // Get display names of selected users
  const getSelectedUsersDisplay = () => {
    if (assignedTo.length === 0) return t('notifications.allUsers');
    if (assignedTo.length === 1) {
      const member = teamMembers.find(m => m.id === assignedTo[0]);
      return member?.name || member?.email?.split('@')[0] || '1 user';
    }
    return `${assignedTo.length} users selected`;
  };

  const toggleAssignedUser = (userId: string) => {
    setAssignedTo((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError(t('notifications.titleRequired') || 'Title is required');
      return;
    }

    if (!description.trim()) {
      setError(t('notifications.descriptionRequired') || 'Description is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Only include dueAt for task_reminder type
      const dueAtValue = notificationType === 'task_reminder' ? (dueAt || null) : null;

      if (mode === 'create') {
        await createNotification({
          restaurantId,
          title: title.trim(),
          description: description.trim(),
          notificationType,
          assignedTo,
          dueAt: dueAtValue,
        });
      } else if (notification) {
        await updateNotification({
          id: notification.id,
          title: title.trim(),
          description: description.trim(),
          notificationType,
          assignedTo,
          dueAt: dueAtValue,
        });
      }
      await onSave();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to save notification');
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-2xl border border-zinc-200 dark:border-white/20 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('notifications.createNotification') : t('notifications.editNotification')}
            </h2>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Notification Type */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('notifications.type')}
              </label>
              <div className="flex flex-wrap gap-2">
                {NOTIFICATION_TYPES.map((type) => {
                  const typeInfo = NOTIFICATION_TYPE_INFO[type];
                  const isSelected = notificationType === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setNotificationType(type)}
                      className={cn(
                        'px-3 py-2 rounded-lg border transition-all',
                        isSelected
                          ? cn('border-2 border-green-500 dark:border-green-600', typeInfo.bgColor, typeInfo.color)
                          : cn('border', borderPrimary, 'hover:bg-gray-50 dark:hover:bg-gray-800')
                      )}
                    >
                      <span className={cn(smallTextSize, semiBoldText)}>
                        {t(typeInfo.label)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('notifications.titleLabel')}
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={cn(
                  'w-full px-4 py-2 rounded-xl border',
                  borderPrimary,
                  'bg-white dark:bg-zinc-900',
                  'text-zinc-900 dark:text-zinc-50',
                  'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                )}
                placeholder={t('notifications.titlePlaceholder')}
              />
            </div>

            {/* Description */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('notifications.descriptionLabel')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className={cn(
                  'w-full px-4 py-2 rounded-xl border',
                  borderPrimary,
                  'bg-white dark:bg-zinc-900',
                  'text-zinc-900 dark:text-zinc-50',
                  'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500',
                  'resize-none'
                )}
                placeholder={t('notifications.descriptionPlaceholder')}
              />
            </div>

            {/* Due Date (optional, mainly for task_reminder) */}
            {notificationType === 'task_reminder' && (
              <div>
                <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                  {t('notifications.dueAtLabel')}
                  <span className={cn(smallTextSize, secondaryText, 'ml-1')}>
                    ({t('common.optional')})
                  </span>
                </label>
                <input
                  type="datetime-local"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  className={cn(
                    'w-full px-4 py-2 rounded-xl border',
                    borderPrimary,
                    'bg-white dark:bg-zinc-900',
                    'text-zinc-900 dark:text-zinc-50',
                    'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                  )}
                />
              </div>
            )}

            {/* Assigned To */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('notifications.assignedToLabel')}
                <span className={cn(smallTextSize, secondaryText, 'ml-1')}>
                  ({t('notifications.assignedToHint')})
                </span>
              </label>
              {teamMembers.length === 0 ? (
                <p className={cn(smallTextSize, secondaryText, 'py-2')}>
                  {t('notifications.noTeamMembers')}
                </p>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  {/* Dropdown trigger */}
                  <button
                    type="button"
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className={cn(
                      'w-full px-4 py-2 rounded-xl border flex items-center justify-between',
                      borderPrimary,
                      'bg-white dark:bg-zinc-900',
                      'text-zinc-900 dark:text-zinc-50',
                      'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                    )}
                  >
                    <span className={cn(smallTextSize, assignedTo.length === 0 && secondaryText)}>
                      {getSelectedUsersDisplay()}
                    </span>
                    <ChevronDown className={cn('w-4 h-4', secondaryText, isUserDropdownOpen && 'rotate-180 transition-transform')} />
                  </button>

                  {/* Dropdown menu - opens above */}
                  {isUserDropdownOpen && (
                    <div className="absolute z-50 bottom-full mb-1 w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg max-h-64 overflow-hidden">
                      {/* Search input */}
                      <div className="p-2 border-b border-zinc-200 dark:border-zinc-700">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
                          <input
                            type="text"
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            placeholder="Search users..."
                            className={cn(
                              'w-full pl-9 pr-3 py-1.5 rounded-lg border',
                              borderPrimary,
                              'bg-white dark:bg-zinc-800',
                              'text-zinc-900 dark:text-zinc-50',
                              'focus:outline-none focus:ring-1 focus:ring-green-500 dark:focus:ring-green-500',
                              smallTextSize
                            )}
                          />
                        </div>
                      </div>

                      {/* User list */}
                      <div className="max-h-48 overflow-y-auto">
                        {filteredTeamMembers.length === 0 ? (
                          <p className={cn(smallTextSize, secondaryText, 'p-3 text-center')}>
                            No users found
                          </p>
                        ) : (
                          filteredTeamMembers.map((member) => {
                            const isSelected = assignedTo.includes(member.id);
                            const displayName = member.name || member.email?.split('@')[0] || `User-${member.id.slice(-4)}`;
                            return (
                              <button
                                key={member.id}
                                type="button"
                                onClick={() => toggleAssignedUser(member.id)}
                                className={cn(
                                  'w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors',
                                  'text-left'
                                )}
                              >
                                <div className={cn(
                                  'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0',
                                  isSelected
                                    ? 'bg-green-500 dark:bg-green-500 border-green-500 dark:border-green-500'
                                    : 'border-zinc-300 dark:border-zinc-600'
                                )}>
                                  {isSelected && <Check className="w-3 h-3 text-white dark:text-white" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={cn(smallTextSize, primaryText, 'truncate')}>
                                    {displayName}
                                  </p>
                                  {member.email && member.name && (
                                    <p className={cn('text-xs', secondaryText, 'truncate')}>
                                      {member.email}
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>

                      {/* Clear selection */}
                      {assignedTo.length > 0 && (
                        <div className="p-2 border-t border-zinc-200 dark:border-zinc-700">
                          <button
                            type="button"
                            onClick={() => setAssignedTo([])}
                            className={cn(smallTextSize, 'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 w-full text-center py-1')}
                          >
                            Clear selection (show to all)
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {assignedTo.length === 0 && (
                <p className={cn(smallTextSize, secondaryText, 'mt-1')}>
                  {t('notifications.visibleToAll')}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-zinc-200 dark:border-zinc-700">
            <button
              onClick={onClose}
              disabled={saving}
              className={cn(
                'flex-1 px-6 py-3 rounded-xl',
                'border',
                borderPrimary,
                secondaryText,
                'hover:bg-gray-50 dark:hover:bg-gray-800',
                'disabled:opacity-50'
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={cn(
                'flex-1 px-6 py-3 rounded-xl',
                'bg-darkGreenColor text-white dark:bg-darkGreenColor-dark dark:text-white',
                'hover:bg-darkGreenColor/95',
                'disabled:opacity-50'
              )}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
